import { useCallback, useRef } from "react";

type MessageHandler = (data: { type: string; line?: string }) => void;

interface UseAiMapWebSocketOptions {
  onMessage?: MessageHandler;
  onOpen?: () => void;
  onClose?: () => void;
  onError?: (error: Event) => void;
  // Called when a drop was unexpected and a reconnect attempt is scheduled
  onReconnecting?: (attempt: number) => void;
  // Called after exhausting all reconnect attempts without success
  onReconnectFailed?: () => void;
}

const WS_URL = "wss://2jmom5ndk6.execute-api.eu-west-3.amazonaws.com/dev";
const MAX_RECONNECT_ATTEMPTS = 5;
const BASE_RECONNECT_DELAY_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 10000;

export function useAiMapWebSocket(options: UseAiMapWebSocketOptions = {}) {
  const wsRef = useRef<WebSocket | null>(null);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // Message resent on every (re)connection, including automatic reconnects
  const initialMessageRef = useRef<Record<string, unknown> | undefined>(undefined);
  // Explicit disconnect()/server-driven close ("done"/"error"/"close") should not trigger a reconnect
  const isIntentionalCloseRef = useRef(false);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearReconnectTimeout = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  const openSocket = useCallback(() => {
    const ws = new WebSocket(WS_URL);

    ws.onopen = () => {
      reconnectAttemptsRef.current = 0;
      if (initialMessageRef.current) {
        ws.send(JSON.stringify(initialMessageRef.current));
      }
      optionsRef.current.onOpen?.();
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        optionsRef.current.onMessage?.(data);

        const shouldClose = data.type === "done" || data.type === "error" || data.action === "close" || data.data?.status === "close";
        console.log("[WebSocket] shouldClose:", shouldClose, "data.data?.status:", data.data?.status);
        if (shouldClose) {
          isIntentionalCloseRef.current = true;
          ws.close();
        }
      } catch {
        console.error("Failed to parse WebSocket message:", event.data);
      }
    };

    ws.onerror = (error) => {
      optionsRef.current.onError?.(error);
    };

    ws.onclose = () => {
      wsRef.current = null;
      optionsRef.current.onClose?.();

      if (isIntentionalCloseRef.current) {
        return;
      }

      if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
        optionsRef.current.onReconnectFailed?.();
        return;
      }

      reconnectAttemptsRef.current += 1;
      const delay = Math.min(
        BASE_RECONNECT_DELAY_MS * 2 ** (reconnectAttemptsRef.current - 1),
        MAX_RECONNECT_DELAY_MS,
      );
      optionsRef.current.onReconnecting?.(reconnectAttemptsRef.current);
      clearReconnectTimeout();
      reconnectTimeoutRef.current = setTimeout(() => {
        wsRef.current = openSocket();
      }, delay);
    };

    return ws;
  }, [clearReconnectTimeout]);

  const connect = useCallback((initialMessage?: Record<string, unknown>) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    clearReconnectTimeout();
    isIntentionalCloseRef.current = false;
    reconnectAttemptsRef.current = 0;
    initialMessageRef.current = initialMessage;

    wsRef.current = openSocket();
  }, [clearReconnectTimeout, openSocket]);

  const disconnect = useCallback(() => {
    clearReconnectTimeout();
    isIntentionalCloseRef.current = true;
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  }, [clearReconnectTimeout]);

  const send = useCallback((data: Record<string, unknown>) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data));
    }
  }, []);

  const isConnected = useCallback(() => wsRef.current?.readyState === WebSocket.OPEN, []);

  return { connect, disconnect, send, isConnected };
}
